import {
  CorrectDocumentBoundariesSchema,
  DocumentFollowUpCommandSchema,
  DocumentTimingCommandSchema,
  IncomingDocumentDetailSchema,
  IncomingDocumentListEntrySchema,
  type IncomingDocumentMimeSchema,
  type IncomingDocumentSourceSchema,
  IncomingOriginalSchema,
  type IncomingReportCommand,
  IncomingReportCommandSchema,
  IncomingReportSchema,
  IncomingReportSummarySchema,
  ReserveIncomingDocumentSchema,
} from "@emr/contracts";
import { z } from "zod";

/** `sha256:<hex>`, the content hash the document store keys originals by. */
async function contentHashOf(bytes: BufferSource): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return `sha256:${Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

const CandidateSchema = z
  .object({
    expectedEventId: z.string(),
    version: z.number(),
    eventType: z.string(),
    requestedCodes: z.array(z.string()),
    ownerId: z.string(),
    orderedAt: z.string(),
    source: z.string().nullable(),
    referral: z
      .object({
        referralId: z.string(),
        state: z.string(),
        destinationFax: z.string().nullable(),
        specialty: z.string().nullable(),
      })
      .optional(),
  })
  .passthrough();
export const IncomingReviewContextSchema = z
  .object({
    report: IncomingReportSchema,
    original: IncomingOriginalSchema,
    contextHash: z.string(),
    sections: z.record(z.string(), z.unknown()),
    candidates: z.array(CandidateSchema),
    matches: z.array(
      z
        .object({
          expected_event_id: z.string(),
          state: z.string(),
          supported_codes: z.array(z.string()),
          evidence: z.unknown(),
        })
        .passthrough(),
    ),
    linkedReferrals: z
      .array(
        z.object({
          referralId: z.string(),
          displayName: z.string(),
          status: z.string(),
        }),
      )
      .optional(),
    reconciliation: z
      .object({
        relevance: z.string(),
        nextSteps: z.array(
          z.object({
            description: z.string(),
            evidence: z.array(z.object({ page: z.number(), quote: z.string() })),
          }),
        ),
      })
      .passthrough()
      .nullable(),
    chartDecisions: z.array(
      z.object({ proposal_index: z.number(), decision: z.string() }).passthrough(),
    ),
    followUps: z.array(
      z
        .object({ id: z.string(), summary: z.string(), dueAt: z.string(), status: z.string() })
        .passthrough(),
    ),
    physicians: z.array(z.object({ id: z.string(), name: z.string().nullable() })),
    staff: z.array(z.object({ id: z.string(), name: z.string().nullable() })).default([]),
    followUpEvents: z.array(
      z.object({ id: z.string(), status: z.string(), eventType: z.string() }),
    ),
  })
  .strict();
export const DocumentInboxSchema = z
  .object({
    reports: z.array(
      z.object({
        report: IncomingReportSummarySchema,
        patientName: z.string(),
        receivedAt: z.string(),
        /** A legible, uncancelled extracted value is flagged abnormal or critical. */
        abnormal: z.boolean().default(false),
        /** The open staff request handing this report to someone, who then sees the row too. */
        handoff: z
          .object({
            requestId: z.string(),
            status: z.string(),
            version: z.number(),
            assignedTo: z.string().optional(),
            assignedToName: z.string().optional(),
          })
          .optional(),
      }),
    ),
    processing: z.array(IncomingOriginalSchema.extend({ patientName: z.string().optional() })),
    obligations: z.array(
      z.object({
        id: z.string(),
        version: z.number(),
        patientId: z.string(),
        patientName: z.string(),
        encounterId: z.string(),
        eventType: z.string(),
        status: z.string(),
        title: z.string().default(""),
        createdAt: z.string().nullable().optional(),
        completedAt: z.string().nullable().optional(),
        requestedTests: z.array(z.string()).default([]),
        receivedTests: z.array(z.string()).default([]),
        remainingTests: z.array(z.string()).default([]),
        source: z.string().nullable().default(null),
        received: z.boolean().default(false),
        receivedReportIds: z.array(z.string()).default([]),
        ownerId: z.string(),
        backupOwnerId: z.string().nullable(),
        coveragePolicyId: z.string(),
        partiallyReceived: z.boolean(),
        timing: z.object({
          configured: z.boolean(),
          waitingForDependency: z.boolean(),
          urgency: z.string(),
          plannedAt: z.string().nullable(),
          dependencyEventId: z.string().nullable(),
          expectedBy: z.string().nullable(),
          overdueAt: z.string().nullable(),
          staffOwnerId: z.string().nullable(),
        }),
        task: z
          .object({
            id: z.string(),
            version: z.number(),
            ownerId: z.string(),
            state: z.string(),
            contactDraft: z.string(),
            nextFollowUpAt: z.string(),
            createdAt: z.string().nullable().optional(),
          })
          .nullable(),
        contacts: z.array(
          z.object({
            id: z.string(),
            outcome: z.string(),
            note: z.string(),
            nextFollowUpAt: z.string(),
            recordedAt: z.string(),
          }),
        ),
      }),
    ),
    alerts: z.array(
      z.object({
        reportId: z.string(),
        originalId: z.string(),
        patientId: z.string(),
        ownerId: z.string().nullable(),
        title: z.string(),
        evidence: z.array(z.object({ page: z.number(), quote: z.string() })),
      }),
    ),
    followUps: z.array(
      z
        .object({
          id: z.string(),
          patientId: z.string(),
          reportId: z.string(),
          summary: z.string(),
          dueAt: z.string(),
          status: z.string(),
          createdAt: z.string().nullable().optional(),
          completedAt: z.string().nullable().optional(),
          ownerUserId: z.string().nullable().optional(),
          ownerId: z.string().nullable().optional(),
          reportVersion: z.number().optional(),
          patientName: z.string().optional(),
        })
        .passthrough(),
    ),
    timingRules: z.array(
      z.object({
        eventType: z.string(),
        urgency: z.string(),
        expectedHours: z.number(),
        overdueHours: z.number(),
        staffOwnerId: z.string(),
        version: z.number(),
      }),
    ),
    staff: z.array(z.object({ id: z.string(), name: z.string().nullable(), role: z.string() })),
  })
  .strict();
export type IncomingReviewContext = z.infer<typeof IncomingReviewContextSchema>;
export type DocumentInbox = z.infer<typeof DocumentInboxSchema>;

export function createIncomingDocumentsClient(
  request: (path: string, init?: RequestInit) => Promise<Response>,
) {
  async function checked(response: Response) {
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(
        body?.error?.message ??
          body?.message ??
          "Document request failed. Your work has been preserved; retry or reload.",
      );
    }
    return response;
  }
  async function json<T>(path: string, schema: z.ZodType<T>, body?: unknown) {
    const response = await checked(
      await request(
        path,
        body === undefined
          ? undefined
          : {
              method: "POST",
              headers: { "content-type": "application/json" },
              body: JSON.stringify(body),
            },
      ),
    );
    return schema.parse(await response.json());
  }
  return {
    listIncomingDocuments: (patientId: string) =>
      json(
        `/v1/patients/${encodeURIComponent(patientId)}/incoming-documents`,
        z.array(IncomingDocumentListEntrySchema),
      ),
    getIncomingDocument: (id: string) =>
      json(`/v1/incoming-documents/${encodeURIComponent(id)}`, IncomingDocumentDetailSchema),
    async uploadIncomingDocument(
      patientId: string,
      file: File,
      encounterId?: string,
      key: string = crypto.randomUUID(),
    ) {
      const bytes = await file.arrayBuffer();
      const contentHash = await contentHashOf(bytes);
      const mimeType =
        file.type ||
        (/\.pdf$/i.test(file.name)
          ? "application/pdf"
          : /\.png$/i.test(file.name)
            ? "image/png"
            : "image/jpeg");
      const reserved = await json(
        "/v1/incoming-documents/reservations",
        IncomingDocumentDetailSchema,
        {
          patientId,
          ...(encounterId ? { encounterId } : {}),
          fileName: file.name,
          byteSize: file.size,
          mimeType,
          contentHash,
          idempotencyKey: `${key}:reserve`,
        },
      );
      if (reserved.original.status !== "reserved") return reserved;
      const uploaded = await checked(
        await request(`/v1/incoming-documents/${reserved.original.id}/original`, {
          method: "PUT",
          headers: {
            "content-type": mimeType,
            "if-match": String(reserved.original.version),
            "idempotency-key": `${key}:finalize`,
          },
          body: bytes,
        }),
      );
      return IncomingDocumentDetailSchema.parse(await uploaded.json());
    },
    /**
     * Files a document from another EMR on the patient's chart as migrated
     * (EXECDEV-227): the bytes as they were, with that EMR's filing details,
     * filed without extraction, so nothing is read by a model or reaches
     * anyone's inbox. The same bytes for the same patient stay one document
     * however often they are sent, and a retry finds its own earlier answer.
     */
    async fileMigratedDocument(
      patientId: string,
      document: {
        readonly bytes: ArrayBuffer;
        readonly fileName: string;
        readonly mimeType: z.infer<typeof IncomingDocumentMimeSchema>;
        readonly source: z.infer<typeof IncomingDocumentSourceSchema>;
      },
    ) {
      const reservation = {
        patientId,
        fileName: document.fileName,
        mimeType: document.mimeType,
        byteSize: document.bytes.byteLength,
        contentHash: await contentHashOf(document.bytes),
        arrivalChannel: "migration" as const,
        processingMode: "file_only" as const,
        source: document.source,
      };
      // The key follows from the patient, the bytes and their filing details.
      const key = `migration:${(await contentHashOf(new TextEncoder().encode(JSON.stringify(reservation)))).slice(7, 55)}`;
      const reserved = await json(
        "/v1/incoming-documents/reservations",
        IncomingDocumentDetailSchema,
        ReserveIncomingDocumentSchema.parse({ ...reservation, idempotencyKey: `${key}:reserve` }),
      );
      if (reserved.original.status !== "reserved") return reserved;
      const uploaded = await checked(
        await request(
          `/v1/incoming-documents/${encodeURIComponent(reserved.original.id)}/original`,
          {
            method: "PUT",
            headers: {
              "content-type": document.mimeType,
              "if-match": String(reserved.original.version),
              "idempotency-key": `${key}:finalize`,
            },
            body: document.bytes,
          },
        ),
      );
      return IncomingDocumentDetailSchema.parse(await uploaded.json());
    },
    async downloadIncomingDocument(id: string) {
      return (
        await checked(await request(`/v1/incoming-documents/${encodeURIComponent(id)}/original`))
      ).blob();
    },
    retryIncomingDocument: (
      id: string,
      expectedVersion: number,
      reprocess = false,
      key: string = crypto.randomUUID(),
    ) =>
      json(
        `/v1/incoming-documents/${encodeURIComponent(id)}/${reprocess ? "reprocess" : "retry"}`,
        IncomingDocumentDetailSchema,
        { expectedVersion, idempotencyKey: key },
      ),
    correctIncomingBoundaries: (
      id: string,
      input: z.input<typeof CorrectDocumentBoundariesSchema>,
    ) =>
      json(
        `/v1/incoming-documents/${encodeURIComponent(id)}/boundaries`,
        IncomingDocumentDetailSchema,
        CorrectDocumentBoundariesSchema.parse(input),
      ),
    getIncomingReportReview: (id: string) =>
      json(
        `/v1/incoming-reports/${encodeURIComponent(id)}/review-context`,
        IncomingReviewContextSchema,
      ),
    getIncomingReportVersions: (id: string) =>
      json(
        `/v1/incoming-reports/${encodeURIComponent(id)}/versions`,
        z.array(IncomingReportSchema),
      ),
    decideIncomingReport: (id: string, input: IncomingReportCommand) =>
      json(
        `/v1/incoming-reports/${encodeURIComponent(id)}/decisions`,
        IncomingReviewContextSchema,
        IncomingReportCommandSchema.parse(input),
      ),
    getDocumentInbox: () => json("/v1/document-follow-up-inbox", DocumentInboxSchema),
    getIncomingDocumentAlerts: () =>
      json("/v1/incoming-document-alerts", DocumentInboxSchema.shape.alerts),
    updateDocumentObligation: (id: string, input: z.input<typeof DocumentFollowUpCommandSchema>) =>
      json(
        `/v1/document-obligations/${encodeURIComponent(id)}/decisions`,
        DocumentInboxSchema,
        DocumentFollowUpCommandSchema.parse(input),
      ),
    updateDocumentTiming: (input: z.input<typeof DocumentTimingCommandSchema>) =>
      json("/v1/document-timing", DocumentInboxSchema, DocumentTimingCommandSchema.parse(input)),
    getIncomingDocumentHistory: (id: string) =>
      json(
        `/v1/incoming-documents/${encodeURIComponent(id)}/history`,
        z.array(
          z.object({
            id: z.number(),
            event: z.string(),
            actorId: z.string().nullable(),
            reportId: z.string().nullable(),
            reportVersion: z.number().nullable(),
            detail: z.unknown(),
            occurredAt: z.string(),
          }),
        ),
      ),
  };
}
export type IncomingDocumentsApi = ReturnType<typeof createIncomingDocumentsClient>;
